import { buildSpecs } from './buildSpecs.js';
import { sharedFoodServiceBenefits } from './sharedBenefits.js';
import {
  PRODUCT_IMAGES,
  overlapHero,
  cardGridHero,
  presentationHero,
  foodWrapHero,
  bannerOverlayHero,
  peCoatedHero,
  cupSleeveHero,
  rippleSleeveHero,
  sugarSachetHero,
} from './productHeroHelpers.js';
import { paperPlatesHeroContent, paperPlatesSpecs, paperPlatesBenefits } from './paperPlates.jsx';
import { pouchesRollsHeroContent, pouchesRollsSpecs, pouchesRollsBenefits } from './pouchesRolls.jsx';
import { notebookCoverJumboRollsSpecs, notebookCoverJumboRollsBenefits } from './notebookCoverJumboRolls.jsx';
import { peSilverCoatedEnvelopePaperHeroContent, peSilverCoatedEnvelopePaperSpecs, peSilverCoatedEnvelopePaperBenefits } from './peSilverCoatedEnvelopePaper.jsx';
import { foodWrapPaperSpecs } from './foodWrapPaper.jsx';
import { cupBottomsHeroContent } from './cupBottoms.jsx';
import { containerSleevesHeroContent } from './containerSleeves.jsx';
import { boxTraySleevesHeroContent } from './boxTraySleeves.jsx';
import { peCoatedRollsHeroContent } from './peCoatedRolls.jsx';

const fourGridImages = (a, b, c, d) => [
  { src: a, alt: 'Product showcase one' },
  { src: b, alt: 'Product showcase two' },
  { src: c, alt: 'Product showcase three' },
  { src: d, alt: 'Product showcase four' },
];

export const productPages = {
  paperPlates: {
    heroVariant: 'paperPlates',
    hero: paperPlatesHeroContent,
    specs: paperPlatesSpecs,
    benefits: paperPlatesBenefits,
    benefitsSubtitle:
      'Key advantages that make our paper plates the preferred choice for food service, catering, and hospitality businesses.',
  },
  peSilverCoatedEnvelopePaper: {
    heroVariant: 'silverEnvelope',
    hero: peSilverCoatedEnvelopePaperHeroContent,
    specs: peSilverCoatedEnvelopePaperSpecs,
    benefits: peSilverCoatedEnvelopePaperBenefits,
    benefitsSubtitle:
      'Key advantages that make our PE silver coated envelope paper the preferred choice for luxury stationery and premium packaging.',
  },
  foodWrapPaper: {
    heroVariant: 'bentoSplit',
    hero: foodWrapHero(
      'Eco-Friendly Solution',
      'Our Sustainable',
      'Food Wrap Paper',
      [
        'Crafted to deliver freshness, hygiene, and sustainable performance, our food wrap paper combines premium quality with functional design for modern food packaging needs.',
        'From grease-resistant wraps for cafés and restaurants to moisture-protective papers and custom printed packaging, our collection preserves quality and strengthens brand appeal.',
      ],
      [
        '100% Food-Grade Safe Materials',
        'Grease & Moisture Resistant',
        'Eco-Friendly & Compostable',
        'Custom Printing Available',
      ],
      PRODUCT_IMAGES.foodWrap,
      PRODUCT_IMAGES.foodWrap4
    ),
    specs: foodWrapPaperSpecs,
    benefits: sharedFoodServiceBenefits,
    benefitsSubtitle:
      'Key advantages that make our food wrap paper the preferred choice for quality-conscious food brands worldwide.',
  },
  notebookCoverJumboRolls: {
    heroVariant: 'presentation',
    hero: presentationHero(
      'Premium Quality Solution',
      'Our Premium',
      'Notebook Cover Jumbo Rolls',
      'High-Volume Quality & Performance',
      [
        'Engineered for high-speed printing and lamination, our notebook cover jumbo rolls combine premium finish with consistent runnability for modern stationery and publishing lines.',
        'From school copies and stationery brands to publishing units, our jumbo rolls reduce cutting loss and support vibrant covers with dependable bulk supply across India.',
      ],
      [
        { src: PRODUCT_IMAGES.notebook1, alt: 'Notebook cover jumbo roll manufacturing' },
        { src: PRODUCT_IMAGES.notebook2, alt: 'Premium notebook cover paper rolls' },
      ]
    ),
    specs: notebookCoverJumboRollsSpecs,
    benefits: notebookCoverJumboRollsBenefits,
    benefitsSubtitle:
      'Key advantages that make our notebook cover jumbo rolls the preferred choice for publishers and stationery manufacturers.',
  },
  peCoatedRolls: {
    heroVariant: 'peCoatedRoll',
    hero: peCoatedRollsHeroContent,
    specs: buildSpecs({
      type: 'PE Coated Paper',
      coating: 'PE Coated Food Grade',
      material: 'Pure Virgin Wood Pulp',
      gsm: '140 - 320 GSM',
      usage: 'Food Packaging, Cup Making, Liquid Wrapping',
      feature: 'Greaseproof, Heat Resistant, Moisture Proof, Waterproof',
      industrialUse: 'Cup Making & Punching',
    }),
    benefits: [
      {
        title: 'Moisture Barrier',
        description:
          'Helps prevent liquid penetration with an efficient polyethylene barrier layer for high-moisture operations.',
      },
      {
        title: 'Heat Sealability',
        description:
          'Delivers a tight, clean seal formulation that enhances packaging speed and keeps products fresh.',
      },
      {
        title: 'Tensile Strength',
        description:
          'Makes industrial wrapping efficient for high-speed automated machinery with reliable performance.',
      },
      {
        title: 'Food Safe Compliance',
        description:
          'Made from food-grade certified virgin base paper that meets modern sustainability standards.',
      },
    ],
    benefitsSubtitle:
      'Key advantages that make our PE coated rolls the preferred choice for food packaging and converting units.',
  },
  pouchesRolls: {
    heroVariant: 'pouchesRoll',
    hero: pouchesRollsHeroContent,
    specs: pouchesRollsSpecs,
    benefits: pouchesRollsBenefits,
    benefitsSubtitle:
      'Key advantages that make our pouches and rolls the preferred choice for flexible packaging and converting units.',
  },
  bakerySolutions: {
    heroVariant: 'foodWrap',
    hero: foodWrapHero(
      'Bakery Grade Solution',
      'Our Premium',
      'Bakery Solutions',
      [
        'Heat-stable and grease-resistant, our bakery solutions paper combines reliable barrier performance with functional design for breads, pastries, and commercial bakery packaging needs.',
        'From artisan bakeries and cafés to catering and takeaway brands, our paper preserves crispness, prevents oil bleed-through, and strengthens presentation across bulk orders.',
      ],
      ['Heat Stable', 'Grease Resistant', 'Food Grade Safe', 'Custom Sizes Available'],
      PRODUCT_IMAGES.foodWrap3,
      PRODUCT_IMAGES.foodWrap4
    ),
    specs: buildSpecs({
      type: 'Bakery Solutions Paper',
      coating: 'Heat-Resistant Food-Safe Coating',
      material: 'Heat-Stable Food-Grade Paper',
      usage: 'Bakery Items, Bread, Pastries, Cakes',
      feature: 'Heat Resistant, Greaseproof, Food Grade',
      industrialUse: 'Bakery Packaging & Food Service',
    }),
    benefits: sharedFoodServiceBenefits,
    benefitsSubtitle:
      'Key advantages that make our bakery solutions paper trusted by bakeries and food service operators.',
  },
  paperCupSleeves: {
    heroVariant: 'cupSleeve',
    hero: cupSleeveHero(
      'Premium Quality Solution',
      'Our Premium',
      ['Paper Cup', 'Sleeves'],
      'Superior Heat Insulation & Branding',
      [
        'Ergonomically designed for hot beverages, our paper cup sleeves combine superior heat insulation with premium branding space for modern café and takeaway service.',
        'From specialty coffee chains to QSR outlets, our sleeve paper supports sharp printing, clean die-cutting, and consistent performance on high-speed converting lines.',
      ],
      [
        'Heat Insulation',
        'Ergonomic Grip',
        'Premium Branding',
        'Food Grade Safe',
      ],
      PRODUCT_IMAGES.cupSleeves,
      PRODUCT_IMAGES.cupSleeves2
    ),
    specs: buildSpecs({
      type: 'Paper Cup Sleeves',
      coating: 'Heat-Resistant Food-Safe Coating',
      material: 'Premium Insulation Paper',
      gsm: '130 - 350 GSM',
      usage: 'Coffee Cups, Hot Beverages, Café Service',
      feature: 'Heat Insulation, Ergonomic Design, Premium Branding',
      industrialUse: 'Cup Sleeve Making & Beverage Service',
    }),
    benefits: sharedFoodServiceBenefits,
    benefitsSubtitle:
      'Key advantages that make our paper cup sleeves ideal for beverage and café brands.',
  },
  ripplePaperCupSleeves: {
    heroVariant: 'rippleSleeve',
    hero: rippleSleeveHero(
      'Premium Beverage Solution',
      'Our Premium',
      ['Ripple Paper', 'Cup Sleeves'],
      'Triple-Layer Heat Barrier & Luxury Grip',
      [
        'Triple-layered ripple construction delivers the highest heat barrier and a luxury grip for takeaway hot drinks, combining premium tactile finish with reliable insulation.',
        'From specialty coffee chains to premium beverage brands, our ripple sleeves preserve comfort, strengthen shelf appeal, and support consistent bulk converting performance.',
      ],
      [
        'Triple-Layer Build',
        'Luxury Grip Feel',
        'Max Heat Barrier',
        'Premium Branding',
      ],
      PRODUCT_IMAGES.ripple,
      PRODUCT_IMAGES.ripple2
    ),
    specs: buildSpecs({
      type: 'Ripple Paper Cup Sleeves',
      coating: 'Premium Heat-Barrier Coating',
      material: 'Triple-Layer Insulation Paper',
      gsm: '100 - 120 GSM',
      usage: 'Premium Coffee Cups, Luxury Hot Beverages',
      feature: 'Triple-Layered, Luxury Grip, Maximum Heat Barrier',
      industrialUse: 'Ripple Sleeve Manufacturing',
    }),
    benefits: sharedFoodServiceBenefits,
    benefitsSubtitle:
      'Key advantages that make our ripple cup sleeves the choice for premium beverage service.',
  },
  paperBags: {
    heroVariant: 'overlap',
    hero: overlapHero(
      'Paper Bags',
      [
        'Durable and sustainable, our paper bags combine custom printing flexibility with tear-resistant strength for retail packaging and takeaway applications nationwide.',
        'From supermarkets and boutiques to food service outlets, our bags preserve load integrity, support brand visibility, and deliver dependable bulk supply across India.',
      ],
      PRODUCT_IMAGES.packaging1,
      PRODUCT_IMAGES.packaging2,
      'Eco-friendly paper bag material',
      'Retail paper bags manufacturing'
    ),
    specs: buildSpecs({
      type: 'Paper Bags',
      coating: 'Food-Safe Bio-Coating',
      material: 'Durable Kraft Paper',
      usage: 'Retail, Takeaway, Food Packaging',
      feature: 'Tear Resistant, Printable, Eco-Friendly',
      industrialUse: 'Bag Making & Retail Packaging',
    }),
    benefits: sharedFoodServiceBenefits,
    benefitsSubtitle:
      'Key advantages that make our paper bags ideal for retail and takeaway businesses.',
  },
  paperCarryBags: {
    heroVariant: 'cardGrid',
    hero: cardGridHero(
      'Luxury Retail Solution',
      ['Paper Carry', 'Bags'],
      'Premium Load Capacity & Finish',
      [
        'Heavy-duty and premium finished, our paper carry bags combine reinforced handles with excellent load capacity for luxury retail and shopping experiences.',
        'From fashion boutiques and gifting brands to corporate retail lines, our bags preserve structure, support bold printing, and strengthen brand presentation at scale.',
      ],
      fourGridImages(
        PRODUCT_IMAGES.packaging3,
        PRODUCT_IMAGES.packaging1,
        PRODUCT_IMAGES.packaging2,
        PRODUCT_IMAGES.foodWrap3
      )
    ),
    specs: buildSpecs({
      type: 'Paper Carry Bags',
      coating: 'Reinforced Food-Safe Coating',
      material: 'Heavy-Duty Multi-Layer Paper',
      usage: 'Premium Retail, Shopping, Luxury Packaging',
      feature: 'Reinforced Handles, High Load Capacity, Premium Finish',
      industrialUse: 'Carry Bag Manufacturing & Retail',
    }),
    benefits: sharedFoodServiceBenefits,
    benefitsSubtitle:
      'Key advantages that make our paper carry bags ideal for premium retail brands.',
  },
  soapStiffenerPaper: {
    heroVariant: 'bannerOverlay',
    hero: bannerOverlayHero(
      'Specialty Paper Solution',
      'Our Premium',
      'Soap Stiffener Paper',
      [
        'High-stiffness and moisture-resistant, our soap stiffener paper combines superior rigidity with premium print surfaces for luxury soap and hygiene packaging applications.',
        'From personal care brands to rigid carton and wrapper lines, our paper preserves shape retention, protects against moisture, and strengthens shelf-ready presentation.',
      ],
      ['High Stiffness', 'Moisture Resistant', 'Premium Print Surface', 'Eco-Friendly'],
      PRODUCT_IMAGES.soap1,
      PRODUCT_IMAGES.soap2
    ),
    specs: buildSpecs({
      type: 'Soap Stiffener Paper',
      coating: 'Moisture-Resistant Coating',
      material: 'High-Stiffness Kraft Paper',
      gsm: '70 - 90 GSM',
      usage: 'Soap Packaging, Rigid Boxes, Hygiene Products',
      feature: 'Superior Stiffness, Moisture Resistance, Premium Print',
      industrialUse: 'Soap & Hygiene Product Packaging',
    }),
    benefits: [
      {
        title: 'Superior Stiffness',
        description:
          'Exceptional rigidity and structural integrity for premium soap packaging and luxury product presentation.',
      },
      {
        title: 'Moisture Resistance',
        description:
          'Specialized coating provides excellent moisture barrier properties to protect soap and hygiene products.',
      },
      {
        title: 'Premium Print Quality',
        description:
          'Smooth surface ideal for high-resolution printing and embossing for luxury brand packaging.',
      },
      {
        title: 'Eco-Friendly',
        description:
          'Recyclable and biodegradable materials that maintain premium quality with environmental responsibility.',
      },
    ],
    benefitsSubtitle:
      'Key advantages that make our soap stiffener paper ideal for hygiene and luxury packaging.',
  },
  packaging: {
    heroVariant: 'bentoSplit',
    hero: {
      ...foodWrapHero(
        'Industrial Packaging Solution',
        'Our Premium',
        'Packaging Solutions',
        [
          'Versatile and protective, our packaging paper combines multi-layer barrier performance with functional design for diverse industrial and commercial packaging needs.',
          'From food products and FMCG lines to general industrial wrapping, our solutions preserve product safety, support custom specs, and strengthen reliable bulk supply.',
        ],
        [
          'Moisture & Grease Barrier',
          'Multi-Layer Protection',
          'Custom GSM Range',
          'Pan India Supply',
        ],
        PRODUCT_IMAGES.packaging1,
        PRODUCT_IMAGES.packaging2
      ),
      stampLabel: 'Multi-Barrier',
      theme: 'industrial',
    },
    specs: buildSpecs({
      type: 'Packaging Paper',
      coating: 'Multi-Barrier Coating Options',
      material: 'Multi-Layer Packaging Paper',
      usage: 'General Packaging, Food Products, Industrial Use',
      feature: 'Moisture, Grease & Air Barrier, Versatile',
      industrialUse: 'General Packaging & Converting',
    }),
    benefits: [
      {
        title: 'Versatile Applications',
        description:
          'Suitable for a wide range of packaging needs from food products to industrial applications.',
      },
      {
        title: 'Superior Protection',
        description:
          'Multi-layer construction provides excellent barrier against moisture, grease, and contaminants.',
      },
      {
        title: 'Custom Solutions',
        description:
          'Tailored specifications and printing options to meet specific packaging requirements.',
      },
      {
        title: 'Cost Effective',
        description:
          'Optimized material usage and roll formats reduce waste and overall packaging costs.',
      },
    ],
    benefitsSubtitle:
      'Key advantages that make our packaging solutions ideal for diverse industrial applications.',
  },
  sugarSachets: {
    heroVariant: 'sugarSachet',
    hero: sugarSachetHero(
      'Food Service Solution',
      'Our Premium',
      ['Sugar', 'Sachets'],
      'Heat-Sealable & Moisture Protected',
      [
        'Food-grade and heat-sealable, our sugar sachet paper combines excellent sealability with moisture barrier performance for condiment and hospitality packaging needs.',
        'From hotels and restaurants to FMCG sachet lines, our paper preserves print quality, keeps contents dry, and supports consistent high-speed production across India.',
      ],
      [
        'Heat Sealable',
        'Moisture Barrier',
        'Food Grade Safe',
        'High-Speed Production',
      ],
      PRODUCT_IMAGES.sugar1,
      PRODUCT_IMAGES.sugar2
    ),
    specs: buildSpecs({
      type: 'Sugar Sachets Paper',
      coating: 'Heat-Sealable Food-Safe Coating',
      material: 'Food-Grade Sachet Paper',
      gsm: '30 - 100 GSM',
      usage: 'Sugar Sachets, Condiment Packaging',
      feature: 'Heat Sealable, Moisture Barrier, High Print Quality',
      industrialUse: 'Sachet Making & Food Service',
    }),
    benefits: [
      {
        title: 'Food Safety',
        description:
          '100% food-grade materials certified for direct contact with sugar and food products.',
      },
      {
        title: 'Excellent Sealability',
        description:
          'Superior heat-sealing properties for secure sachet production without contamination.',
      },
      {
        title: 'Print Quality',
        description:
          'Smooth surface ideal for branding, nutritional information, and product details.',
      },
      {
        title: 'Moisture Barrier',
        description:
          'Effective moisture protection keeps sugar and powdered products dry and free-flowing.',
      },
    ],
    benefitsSubtitle:
      'Key advantages that make our sugar sachet paper ideal for food service and FMCG brands.',
  },
  cupBottoms: {
    heroVariant: 'cupBottom',
    hero: cupBottomsHeroContent,
    specs: buildSpecs({
      type: 'Cup Bottoms Paper',
      coating: 'Heat-Resistant Food-Safe Coating',
      material: 'Premium Cup Bottom Paper',
      gsm: '140 - 250 GSM',
      usage: 'Paper Cup Bottoms, Beverage Cups',
      feature: 'Heat Resistant, Leak Proof, Form Stable',
      industrialUse: 'Cup Bottom Punching & Cup Making',
    }),
    benefits: [
      {
        title: 'Heat Resistance',
        description:
          'Excellent heat resistance for hot beverage applications without compromising structure.',
      },
      {
        title: 'Leak Prevention',
        description:
          'Superior liquid barrier properties preventing leaks in beverage containers.',
      },
      {
        title: 'Form Stability',
        description:
          'Maintains shape and strength when exposed to hot liquids during cup use.',
      },
      {
        title: 'Food Safe',
        description:
          'Food-grade materials certified for direct contact with hot and cold beverages.',
      },
    ],
    benefitsSubtitle:
      'Key advantages that make our cup bottom paper ideal for cup manufacturers.',
  },
  containerSleeves: {
    heroVariant: 'containerSleeve',
    hero: containerSleevesHeroContent,
    specs: buildSpecs({
      type: 'Container Sleeves',
      coating: 'Protective Food-Safe Coating',
      material: 'Premium Container Sleeve Paper',
      gsm: '150 - 350 GSM',
      usage: 'Food Containers, Takeaway Packaging',
      feature: 'Thermal Insulation, Moisture Barrier, Printable',
      industrialUse: 'Container Sleeve Making & Food Service',
    }),
    benefits: [
      {
        title: 'Insulation Properties',
        description:
          'Excellent thermal insulation keeping food hot or cold during transport and storage.',
      },
      {
        title: 'Moisture Barrier',
        description:
          'Superior moisture resistance preventing leaks and maintaining food freshness.',
      },
      {
        title: 'Structural Strength',
        description:
          'High tensile strength for reliable performance in heavy food container applications.',
      },
      {
        title: 'Customizable Design',
        description:
          'Ideal surface for custom printing and branding for food service presentation.',
      },
    ],
    benefitsSubtitle:
      'Key advantages that make our container sleeves ideal for takeaway food brands.',
  },
  boxTraySleeves: {
    heroVariant: 'boxTraySleeve',
    hero: boxTraySleevesHeroContent,
    specs: buildSpecs({
      type: 'Box & Tray Sleeves',
      coating: 'Food-Safe Protective Coating',
      material: 'Premium Box Tray Paper',
      gsm: '150 - 350 GSM',
      usage: 'Box Trays, Food Service Packaging',
      feature: 'Structural Integrity, Grease Resistant, Heat Tolerant',
      industrialUse: 'Tray Sleeve Making & Food Service',
    }),
    benefits: [
      {
        title: 'Structural Integrity',
        description:
          'Excellent rigidity and strength for box tray applications requiring reliable support.',
      },
      {
        title: 'Grease Resistance',
        description:
          'Superior grease and oil resistance for food service and bakery applications.',
      },
      {
        title: 'Heat Tolerance',
        description:
          'Withstands moderate heat for warm food packaging and bakery items.',
      },
      {
        title: 'Premium Print Surface',
        description:
          'Smooth surface for high-quality printing and branding for food establishments.',
      },
    ],
    benefitsSubtitle:
      'Key advantages that make our box and tray sleeves ideal for food service packaging.',
  },
};

export const getProductPageConfig = (key) => productPages[key];
